<script>
import { defineComponent } from "vue";

import { CLASS_NAME_COMPONENT_FILE_INPUT } from "./common";

export default defineComponent({
  name: "TgFileInput",
  // *Text props can be replaced with SFC i18n once this component is migrated to composition API
  props: {
    inputId: {
      type: String,
      default: undefined
    },
    dropzoneClass: {
      type: [String, Object, Array],
      default: undefined
    },
    hasError: {
      type: Boolean,
      default: false
    },
    browseFilesText: {
      type: String,
      default: undefined
    },
    dropFilesHereText: {
      type: String,
      default: undefined
    }
  },
  emits: ["input"],
  data() {
    return {
      /** @type {File[]} */
      files: [],
      isDropzoneActive: false
    };
  },
  computed: {
    baseClass() {
      return CLASS_NAME_COMPONENT_FILE_INPUT;
    }
  },
  watch: {
    files: function (files) {
      this.$emit("input", files);
    }
  },
  methods: {
    browseFiles() {
      this.$refs.fileInput.click();
    },
    removeFile(index) {
      this.files = this.files.filter((_, i) => i !== index);
    },
    reset() {
      this.files = [];
    },
    onDragOver() {
      this.isDropzoneActive = true;
    },
    onDragLeave() {
      this.isDropzoneActive = false;
    },
    onDropFiles(/** @type {DragEvent} */ event) {
      this.isDropzoneActive = false;

      this.files = [...this.files, ...Array.from(event.dataTransfer.files)];
    },
    onSelectFiles(event) {
      this.files = [...this.files, ...Array.from(event.target.files)];
    },
    formatSize(bytes) {
      const units = ["B", "KB", "MB", "GB", "TB"];
      if (bytes === 0) {
        return `${bytes} ${units[0]})`;
      }

      const i = Math.floor(Math.log(bytes) / Math.log(1024));
      if (i === 0) {
        return `${bytes} ${units[i]}`;
      }

      return `${(bytes / 1024 ** i).toFixed(1)} ${units[i]}`;
    },
    getExtension(filename) {
      return filename.split(".").pop();
    }
  }
});
</script>

<template>
  <div :class="baseClass">
    <input
      :id="inputId"
      ref="fileInput"
      type="file"
      multiple
      class="d-none"
      @input="onSelectFiles($event)"
    />
    <div
      :class="[
        dropzoneClass,
        `${baseClass}__dropzone`,
        { [`${baseClass}__dropzone--active`]: isDropzoneActive },
        { [`${baseClass}__dropzone--error`]: hasError }
      ]"
      @dragover.prevent="onDragOver($event)"
      @dragleave.prevent="onDragLeave($event)"
      @drop.prevent="onDropFiles($event)"
    >
      <svg
        role="presentation"
        xmlns="http://www.w3.org/2000/svg"
        width="25"
        height="25"
        viewBox="0 0 25 25"
      >
        <path
          fill="#7F8B98"
          fill-rule="nonzero"
          d="M19.5652 3.2609c-1.801-1.801-4.7208-1.801-6.5217 0l-8.6957 8.6956c-2.4012 2.4013-2.4012 6.2944 0 8.6957 2.4013 2.4012 6.2944 2.4012 8.6957 0l10.326-10.3261a.7686.7686 0 10-1.0869-1.087l-10.326 10.3261c-1.801 1.801-4.7209 1.801-6.5218 0-1.801-1.801-1.801-4.7208 0-6.5217l8.6956-8.6957c1.2007-1.2006 3.1472-1.2006 4.3479 0 1.2006 1.2006 1.2006 3.1472 0 4.3479l-8.1522 8.1521c-.6003.6003-1.5736.6003-2.174 0-.6002-.6003-.6002-1.5736 0-2.1739l7.0653-7.0652a.7686.7686 0 00-1.087-1.087L7.0652 13.587c-1.2006 1.2006-1.2006 3.1472 0 4.3478 1.2006 1.2006 3.1472 1.2006 4.3478 0l8.1522-8.1522c1.801-1.801 1.801-4.7208 0-6.5217z"
        />
      </svg>
      <button :class="`${baseClass}__browse-button ml-1`" @click.prevent="browseFiles()">
        {{ browseFilesText }}
      </button>
      {{ dropFilesHereText }}
    </div>

    <ul v-if="files.length > 0" :class="`${baseClass}__file-list`">
      <li v-for="(file, index) in files" :key="index" :class="`${baseClass}__file mt-2`">
        <div :class="`${baseClass}__file-name`">
          {{ file.name }}
        </div>
        <div :class="`${baseClass}__file-metadata`">
          {{ formatSize(file.size) }} &bull; {{ getExtension(file.name) }}
        </div>
        <button
          :class="`${baseClass}__file-remove-button`"
          aria-label="Fjern fil"
          @click="removeFile(index)"
        >
          <svg
            role="presentation"
            xmlns="http://www.w3.org/2000/svg"
            width="12"
            height="12"
            viewBox="0 0 12 12"
          >
            <g fill="#E74C3C" fill-rule="nonzero">
              <path
                d="M9.9358.65c.3905-.3905 1.0236-.3905 1.4142 0 .3604.3605.3882.9278.0832 1.32l-.0832.0942L2.0642 11.35c-.3905.3905-1.0236.3905-1.4142 0-.3604-.3605-.3882-.9278-.0832-1.32L.65 9.9358 9.9358.65z"
              />
              <path
                d="M2.0642.65C1.6737.2595 1.0406.2595.65.65c-.3604.3605-.3882.9278-.0832 1.32l.0832.0942L9.9358 11.35c.3905.3905 1.0236.3905 1.4142 0 .3604-.3605.3882-.9278.0832-1.32l-.0832-.0942L2.0642.65z"
              />
            </g>
          </svg>
        </button>
      </li>
    </ul>
  </div>
</template>

<style lang="less" scoped>
@import "@/abc-dsk/theming/index.less";

@primary-color: rgb(@theme-colors[@primary][base]);
@ghost-color: rgb(@theme-colors[@grey][25]);
@border-color: rgb(@theme-colors[@grey][50]);
@bg-color-light: rgb(@theme-colors[@grey][10]);
@font-color-grey-dark: rgb(@theme-colors[@grey][base]);
@font-color-dark: rgb(@theme-colors[@grey][110]);
@error-color: rgb(@theme-colors[@red][base]);

.tg-file-input {
  &__dropzone {
    position: relative;
    padding: 40px 15px;
    overflow: hidden;
    border: 1px solid @border-color;
    border-radius: 4px;
    color: @font-color-grey-dark;
    text-align: center;

    &--active {
      border-style: dashed;
      background: @ghost-color;
    }

    // Disable pointer-events while dragging to avoid flashing
    &--active > * {
      pointer-events: none;
    }

    &--error {
      border-color: @error-color;
      color: @error-color;
    }
  }

  &__browse-button {
    padding: 0;
    border: 0;
    background: none;
    color: @primary-color;
    text-decoration: underline;
    cursor: pointer;
  }

  &__file-list {
    margin: 0;
    padding: 0;
    list-style: none;
  }

  &__file {
    position: relative;
    padding: 10px 30px 10px 15px;
    overflow: hidden;
    border: 1px solid @border-color;
    border-radius: 4px;
    background-color: @bg-color-light;

    &-name {
      color: @font-color-dark;
      font-size: 16px;
      line-height: 22px;
      word-break: break-word;
    }

    &-metadata {
      color: @font-color-grey-dark;
      font-size: 14px;
      line-height: 19px;
    }

    &-remove-button {
      position: absolute;
      top: 10px;
      right: 15px;
      padding: 0;
      border: 0;
      background: none;
      color: @error-color;
      font-size: 12px;
      cursor: pointer;
    }
  }
}
</style>
